import { FixingMode } from "@/data/fixingMode";
import i18n from "@/i18n";
import { useTranslation as useTranslationI18n } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { Form } from "@/components/ui/form";
import { Card, CardBody, CardFooter, CardHeader, Divider } from "@heroui/react";

import {
  AutocompleteAsyncField,
  AutocompleteLabelField,
  DatePickerField,
  InputField,
} from "@/features/FormCard/Fields";

import {
  CancelConfirmationModal,
  ConfirmationModal,
} from "@/features/FormCard/Modals";

import {
  getAssetClassTranslation,
  parseToDateValue,
  preprocessTextAsyncAutocompleteField,
} from "@/features/FormCard/utils";

import { FormActionsNew } from "@/features/FormCard/Actions";

import { useEditForm } from "@/features/Edit/hooks";
import { NewDiff } from "@/features/FormCard/NewDiff";

import { dateSchema } from "@/features/FormCard/utils";
import { Clients } from "@/lib/clients";
import { Tables } from "@/gen/tableNames/tables";
import useCurrencyOptions from "@/hooks/useCurrencyOption";
import { useTranslation } from "@/hooks/useTranslation";
import { useEffect, useMemo } from "react";
import { useWatch } from "react-hook-form";
import { fieldTranslations } from "./translations";
import {
  computeFixingMode,
  requiresFixing,
  requiresMarketValue,
} from "./utils";
import { FormCleaner } from "@/hooks/useCleanWhenHidden";

const FormSchema = z
  .object({
    date: z.preprocess((val) => parseToDateValue(val as string), dateSchema),
    c5_static_datum_id: z.number(),
    c5_static_datum_name: z.preprocess(
      (e: any) => preprocessTextAsyncAutocompleteField(e),
      z.union([
        z.string().nullable().optional(),
        z.object({
          id: z.number(),
          text: z.string(),
          asset_class_1: z.string(),
          asset_class_2: z.string(),
          currency: z.string(),
        }),
      ]),
    ),
    fixing: z.coerce.number().optional(),
    market_value: z.coerce.number().optional(),
    currency: z.string(),
  })
  .refine(requiresMarketValue, {
    message: i18n.t("time_series.errors.market_value_required"),
    path: ["market_value"],
  })
  .refine(requiresFixing, {
    message: i18n.t("time_series.errors.unit_price_required"),
    path: ["fixing"],
  });
const formSchemaFields = FormSchema.keyof().enum;

const client = Clients.C5;
const table_name = Tables.C5_TIME_SERIES;

export function C5TimeSeriesNew() {
  const { t } = useTranslationI18n();

  const navigate = useNavigate();

  const {
    form,
    handleReset,
    onSubmit,
    confirmUpdate,
    cancelUpdate,
    isConfirmationModalOpen,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    isUpdating,
    changedFields,
  } = useEditForm({
    client,
    table_name,
    FormSchema,
    isNew: true,
  });

  const currencyOptions = useCurrencyOptions();

  const navigateToList = () => {
    navigate(-1);
  };

  // Watch c5_static_datum_name field for automatic currency update
  const assetData = useWatch({
    control: form.control,
    name: "c5_static_datum_name",
  });

  const { translations } = useTranslation({
    client,
    table_name: Tables.C5_STATIC_DATA,
    language: "fr",
  });

  const fixingMode: FixingMode | null = useMemo(() => {
    if (typeof assetData !== "object") return null;

    const assetName = assetData?.text ?? undefined;
    const assetClass2 = assetData?.asset_class_2 ?? undefined;

    return computeFixingMode(assetClass2, assetName);
  }, [assetData]);

  // Auto-update currency when asset is selected
  useEffect(() => {
    if (assetData && typeof assetData === "object" && assetData.currency) {
      form.setValue(
        "currency",
        currencyOptions.find((option) => option.value === assetData.currency)
          ?.label,
      );
    }
  }, [assetData, form, currencyOptions]);

  const priceNotApplicable = fixingMode === FixingMode.NA;
  useEffect(() => {
    if (priceNotApplicable) {
      form.setError("c5_static_datum_name" as any, {
        type: "manual",
        message: i18n.t("time_series.errors.price_not_applicable"),
      });
    } else {
      form.clearErrors("c5_static_datum_name" as any);
    }
  }, [priceNotApplicable, form]);

  const ready = !isUpdating;

  const cleanSpecs = useMemo(
    () => [
      { name: "date" as const, isVisible: !priceNotApplicable },
      {
        name: "market_value" as const,
        isVisible: fixingMode === FixingMode.VALORISATION,
      },
      { name: "fixing" as const, isVisible: fixingMode === FixingMode.FIXING },
    ],
    [fixingMode, assetData, priceNotApplicable],
  );

  return (
    <div className="bg-gray-50 h-full">
      <div className="container py-6 h-full max-w-card">
        <Card className="p-4 bg-white w-full">
          <Form {...form}>
            <FormCleaner
              specs={cleanSpecs}
              ready={ready}
              keepRegistered={false}
            />
            <form onSubmit={form.handleSubmit(onSubmit)} className="">
              <CardHeader className="text-lg font-bold text-gray-700">
                {i18n.t("time_series.new_price_valuation")}
              </CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-4">
                <AutocompleteAsyncField
                  name={formSchemaFields.c5_static_datum_name}
                  label={t(`${table_name}.columns.c5_static_datum_name`, {
                    ns: "tables",
                    defaultValue: "Actif",
                  })}
                  control={form.control}
                  isRequired
                  base_url={`${Clients.C5}/${Tables.C5_STATIC_DATA}`}
                  id_column={formSchemaFields.c5_static_datum_id}
                  column_name="name"
                  options={{
                    translateKey: "asset_class_2",
                    client: Clients.C5,
                    table_name: Tables.C5_STATIC_DATA,
                    language: "fr",
                  }}
                />
                {assetData && typeof assetData === "object" && (
                  <div className="flex gap-2 text-sm">
                    <div className="flex gap-1">
                      <span className="font-semibold text-gray-700">
                        Classe d'actif:
                      </span>
                      <span className="text-gray-600">
                        {getAssetClassTranslation(
                          assetData.asset_class_1,
                          "asset_class_1",
                          translations,
                        )}
                        {assetData.asset_class_2 &&
                          ` / ${getAssetClassTranslation(
                            assetData.asset_class_2,
                            "asset_class_2",
                            translations,
                          )}`}
                      </span>
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DatePickerField
                    name={formSchemaFields.date}
                    label={t(`${table_name}.columns.date`, {
                      ns: "tables",
                      defaultValue: "Date",
                    })}
                    isRequired
                    control={form.control}
                    fieldOptions={{
                      isDisabled: priceNotApplicable,
                      isVisible: !priceNotApplicable,
                    }}
                  />
                  <AutocompleteLabelField
                    name={formSchemaFields.currency}
                    label={t(`${table_name}.columns.currency`, {
                      ns: "tables",
                      defaultValue: "Devise",
                    })}
                    options={currencyOptions}
                    control={form.control}
                    className="min-w-0"
                    fieldOptions={{
                      isDisabled: true,
                    }}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {fixingMode === FixingMode.VALORISATION && (
                    <InputField
                      name={formSchemaFields.market_value}
                      label={t(`${table_name}.columns.market_value`, {
                        ns: "tables",
                        defaultValue: "Valorisation",
                      })}
                      isRequired
                      control={form.control}
                      type="number"
                    />
                  )}
                  {fixingMode === FixingMode.FIXING && (
                    <InputField
                      name={formSchemaFields.fixing}
                      label={t(`${table_name}.columns.fixing`, {
                        ns: "tables",
                        defaultValue: "Prix unitaire",
                      })}
                      isRequired
                      control={form.control}
                      type="number"
                    />
                  )}
                </div>
              </CardBody>
              <CardFooter>
                <FormActionsNew
                  onReset={handleReset}
                  onDelete={openDeleteModal}
                  isDirty={!priceNotApplicable}
                />
              </CardFooter>
            </form>
          </Form>
        </Card>
        <ConfirmationModal
          isOpen={!priceNotApplicable && isConfirmationModalOpen}
          isLoading={isUpdating}
          onClose={cancelUpdate}
          onConfirm={confirmUpdate}
          title={t("confirm_create")}
          children={
            changedFields
              ? NewDiff(
                  changedFields,
                  [formSchemaFields.c5_static_datum_id],
                  fieldTranslations,
                )
              : t("no_changes")
          }
        />
        <CancelConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={closeDeleteModal}
          onCancel={navigateToList}
        />
      </div>
    </div>
  );
}
